import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Heart, Users, FileText, AlertCircle, Plus, Calendar, TrendingUp, Trophy, Shield, Activity, Brain, Download, Loader2, ChevronDown, ChevronUp, MessageSquare, Send, Paperclip, CheckSquare } from 'lucide-react';
import WellnessPanel from '../components/counselor/WellnessPanel';
import CounselorGamificationView from '../components/counselor/CounselorGamificationView';
import CompliancePanel from '../components/compliance/CompliancePanel';
import NotificationBell from '../components/shared/NotificationBell';
import { Checkbox } from '@/components/ui/checkbox';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CounselorDashboard = ({ user, onLogout }) => {
  const [students, setStudents] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [emotionalData, setEmotionalData] = useState({ emotional_data: [], checkins: [] });
  const [sessions, setSessions] = useState([]);
  const [interventions, setInterventions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('students');
  const [aiReport, setAiReport] = useState(null);
  const [generatingReport, setGeneratingReport] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [expandedStudent, setExpandedStudent] = useState(null);

  // Feedback state
  const [showFeedbackDialog, setShowFeedbackDialog] = useState(false);
  const [feedbackForm, setFeedbackForm] = useState({ student_id: '', feedback_text: '', send_to_student: true, send_to_parent: true, send_to_teacher: true, attach_report: false, attach_task_report: null });
  const [sendingFeedback, setSendingFeedback] = useState(false);
  const [sentFeedback, setSentFeedback] = useState([]);
  const [counselorAssignments, setCounselorAssignments] = useState([]);

  const [newSession, setNewSession] = useState({
    student_id: '', session_date: '', duration_minutes: 30,
    notes: '', mood_assessment: 'neutral', follow_up_needed: false, follow_up_date: ''
  });
  const [showSessionDialog, setShowSessionDialog] = useState(false);

  const [newIntervention, setNewIntervention] = useState({
    student_id: '', concern: '', intervention_type: 'emotional',
    action_plan: '', start_date: '', review_date: ''
  });
  const [showInterventionDialog, setShowInterventionDialog] = useState(false);

  useEffect(() => {
    fetchStudents();
    fetchSessions();
    fetchInterventions();
    fetchSentFeedback();
    fetchCounselorAssignments();
  }, []);

  const fetchStudents = async () => {
    try {
      const res = await axios.get(`${API}/counselor/students`);
      setStudents(res.data.students || []);
      if (res.data.students?.length > 0) setSelectedStudent(res.data.students[0]);
    } catch { toast.error('Failed to load students'); }
  };

  const fetchSessions = async () => {
    try {
      const res = await axios.get(`${API}/counselor/sessions`);
      setSessions(res.data.sessions || []);
    } catch { /* silent */ }
  };

  const fetchInterventions = async () => {
    try {
      const res = await axios.get(`${API}/counselor/interventions`);
      setInterventions(res.data.interventions || []);
    } catch { /* silent */ }
  };

  useEffect(() => {
    if (selectedStudent) fetchStudentEmotionalData(selectedStudent.id);
  }, [selectedStudent]);

  const fetchStudentEmotionalData = async (studentId) => {
    try {
      const res = await axios.get(`${API}/counselor/student/${studentId}/emotional`);
      setEmotionalData(res.data);
    } catch { /* silent */ }
  };

  const handleCreateSession = async () => {
    if (!newSession.student_id || !newSession.session_date || !newSession.notes) {
      toast.error('Please fill in all required fields'); return;
    }
    try {
      setLoading(true);
      await axios.post(`${API}/counselor/sessions`, newSession);
      toast.success('Session recorded!');
      setShowSessionDialog(false);
      setNewSession({ student_id: '', session_date: '', duration_minutes: 30, notes: '', mood_assessment: 'neutral', follow_up_needed: false, follow_up_date: '' });
      fetchSessions();
    } catch { toast.error('Failed to record session'); }
    finally { setLoading(false); }
  };

  const handleCreateIntervention = async () => {
    if (!newIntervention.student_id || !newIntervention.concern || !newIntervention.action_plan) {
      toast.error('Please fill in all required fields'); return;
    }
    try {
      setLoading(true);
      await axios.post(`${API}/counselor/interventions`, newIntervention);
      toast.success('Intervention plan created!');
      setShowInterventionDialog(false);
      setNewIntervention({ student_id: '', concern: '', intervention_type: 'emotional', action_plan: '', start_date: '', review_date: '' });
      fetchInterventions();
    } catch { toast.error('Failed to create intervention plan'); }
    finally { setLoading(false); }
  };

  const fetchSentFeedback = async () => {
    try { const res = await axios.get(`${API}/counselor/sent-feedback`); setSentFeedback(res.data.feedback || []); } catch { /* silent */ }
  };

  const fetchCounselorAssignments = async () => {
    try { const res = await axios.get(`${API}/counselor/assignments`); setCounselorAssignments(res.data.assignments || []); } catch { /* silent */ }
  };

  const handleSendFeedback = async () => {
    if (!feedbackForm.student_id || !feedbackForm.feedback_text) { toast.error('Select a student and write feedback'); return; }
    if (!feedbackForm.send_to_student && !feedbackForm.send_to_parent && !feedbackForm.send_to_teacher) { toast.error('Select at least one recipient'); return; }
    setSendingFeedback(true);
    try {
      const attachedReport = feedbackForm.attach_report && aiReport ? aiReport : feedbackForm.attach_task_report ? feedbackForm.attach_task_report : null;
      const res = await axios.post(`${API}/counselor/send-feedback`, {
        student_id: feedbackForm.student_id,
        feedback_text: feedbackForm.feedback_text,
        attached_report: attachedReport,
        send_to_student: feedbackForm.send_to_student,
        send_to_parent: feedbackForm.send_to_parent,
        send_to_teacher: feedbackForm.send_to_teacher
      });
      toast.success(res.data.message);
      setShowFeedbackDialog(false);
      setFeedbackForm({ student_id: '', feedback_text: '', send_to_student: true, send_to_parent: true, send_to_teacher: true, attach_report: false, attach_task_report: null });
      fetchSentFeedback();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to send feedback'); }
    finally { setSendingFeedback(false); }
  };

  const getAverageMood = () => {
    if (emotionalData.emotional_data.length === 0) return 'N/A';
    const avg = emotionalData.emotional_data.reduce((s, e) => s + e.sentiment_score, 0) / emotionalData.emotional_data.length;
    if (avg > 0.6) return 'Positive';
    if (avg < 0.4) return 'Needs Support';
    return 'Neutral';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 via-rose-50 to-red-100" data-testid="counselor-dashboard">
      <header className="bg-white/80 backdrop-blur-md border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-br from-pink-500 to-rose-600 rounded-xl flex items-center justify-center">
                <Heart className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }} data-testid="counselor-title">Counselor Dashboard</h1>
                <p className="text-sm text-gray-600">Welcome, {user?.name}!</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <NotificationBell onNavigate={(tab) => setActiveTab(tab)} />
              <Button onClick={onLogout} variant="outline" className="border-gray-300 hover:bg-gray-100" data-testid="logout-btn">Logout</Button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {students.length === 0 ? (
          <Card className="p-12 text-center bg-white/90 backdrop-blur-sm border-0 shadow-lg">
            <div className="w-16 h-16 bg-pink-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Users className="w-8 h-8 text-pink-600" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No students assigned yet</h3>
            <p className="text-gray-600">Please contact a superuser to assign students for counseling</p>
          </Card>
        ) : (
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full grid-cols-7 mb-6" data-testid="counselor-tabs">
              <TabsTrigger value="students" className="flex items-center gap-1 text-xs sm:text-sm" data-testid="tab-students">
                <Users className="w-4 h-4" /><span className="hidden sm:inline">Students</span>
              </TabsTrigger>
              <TabsTrigger value="sessions" className="flex items-center gap-1 text-xs sm:text-sm" data-testid="tab-sessions">
                <Calendar className="w-4 h-4" /><span className="hidden sm:inline">Sessions</span>
              </TabsTrigger>
              <TabsTrigger value="interventions" className="flex items-center gap-1 text-xs sm:text-sm" data-testid="tab-interventions">
                <AlertCircle className="w-4 h-4" /><span className="hidden sm:inline">Interventions</span>
              </TabsTrigger>
              <TabsTrigger value="wellness" className="flex items-center gap-1 text-xs sm:text-sm" data-testid="tab-wellness">
                <Activity className="w-4 h-4" /><span className="hidden sm:inline">Wellness</span>
              </TabsTrigger>
              <TabsTrigger value="gamification" className="flex items-center gap-1 text-xs sm:text-sm" data-testid="tab-gamification">
                <Trophy className="w-4 h-4" /><span className="hidden sm:inline">Gamification</span>
              </TabsTrigger>
              <TabsTrigger value="compliance" className="flex items-center gap-1 text-xs sm:text-sm" data-testid="tab-compliance">
                <Shield className="w-4 h-4" /><span className="hidden sm:inline">Compliance</span>
              </TabsTrigger>
              <TabsTrigger value="analytics" className="flex items-center gap-1 text-xs sm:text-sm" data-testid="tab-analytics">
                <TrendingUp className="w-4 h-4" /><span className="hidden sm:inline">Analytics</span>
              </TabsTrigger>
            </TabsList>

            {/* ===== STUDENTS TAB ===== */}
            <TabsContent value="students" className="space-y-6">
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">Select Student</label>
                <div className="flex gap-3 flex-wrap">
                  {students.map((student) => (
                    <button key={student.id} onClick={() => setSelectedStudent(student)} data-testid={`student-btn-${student.id}`}
                      className={`px-4 py-2 rounded-lg font-medium transition-all ${selectedStudent?.id === student.id ? 'bg-gradient-to-r from-pink-500 to-rose-600 text-white shadow-lg' : 'bg-white text-gray-700 border border-gray-300 hover:border-pink-300'}`}>
                      {student.name}
                    </button>
                  ))}
                </div>
              </div>
              {selectedStudent && (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <Card className="p-6 bg-white/90 backdrop-blur-sm border-0 shadow-lg">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-gray-600 mb-1">Overall Mood</p>
                          <p className="text-2xl font-bold text-pink-600" data-testid="overall-mood">{getAverageMood()}</p>
                        </div>
                        <div className="w-12 h-12 bg-pink-100 rounded-xl flex items-center justify-center">
                          <Heart className="w-6 h-6 text-pink-600" />
                        </div>
                      </div>
                    </Card>
                    <Card className="p-6 bg-white/90 backdrop-blur-sm border-0 shadow-lg">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-gray-600 mb-1">Check-ins</p>
                          <p className="text-3xl font-bold text-rose-600" data-testid="checkins-count">{emotionalData.checkins.length}</p>
                        </div>
                        <div className="w-12 h-12 bg-rose-100 rounded-xl flex items-center justify-center">
                          <FileText className="w-6 h-6 text-rose-600" />
                        </div>
                      </div>
                    </Card>
                  </div>
                  <Card className="bg-white/90 backdrop-blur-sm border-0 shadow-lg">
                    <Tabs defaultValue="checkins" className="w-full">
                      <TabsList className="w-full justify-start border-b rounded-none bg-transparent h-auto p-0">
                        <TabsTrigger value="checkins" className="rounded-none border-b-2 border-transparent data-[state=active]:border-pink-500 data-[state=active]:bg-transparent">Emotional Check-ins</TabsTrigger>
                        <TabsTrigger value="sentiment" className="rounded-none border-b-2 border-transparent data-[state=active]:border-pink-500 data-[state=active]:bg-transparent">Sentiment Analysis</TabsTrigger>
                      </TabsList>
                      <TabsContent value="checkins" className="p-6">
                        {emotionalData.checkins.length === 0 ? (
                          <div className="text-center py-12 text-gray-500"><p>No check-ins recorded yet</p></div>
                        ) : (
                          <div className="space-y-3">
                            {emotionalData.checkins.map((checkin, idx) => (
                              <Card key={idx} className="p-4 border border-gray-200">
                                <div className="flex justify-between items-center">
                                  <div>
                                    <p className="font-medium capitalize">Mood: {checkin.mood}</p>
                                    <p className="text-sm text-gray-600">Energy: {checkin.energy_level}/10</p>
                                    <p className="text-xs text-gray-500 mt-1">{new Date(checkin.timestamp).toLocaleString()}</p>
                                  </div>
                                  <div className="text-3xl">
                                    {checkin.mood === 'happy' && '\u{1F60A}'}
                                    {checkin.mood === 'neutral' && '\u{1F610}'}
                                    {checkin.mood === 'sad' && '\u{1F614}'}
                                    {checkin.mood === 'stressed' && '\u{1F630}'}
                                    {checkin.mood === 'tired' && '\u{1F634}'}
                                  </div>
                                </div>
                              </Card>
                            ))}
                          </div>
                        )}
                      </TabsContent>
                      <TabsContent value="sentiment" className="p-6">
                        {emotionalData.emotional_data.length === 0 ? (
                          <div className="text-center py-12 text-gray-500"><p>No sentiment data available</p></div>
                        ) : (
                          <div className="space-y-3">
                            {emotionalData.emotional_data.slice(0, 10).map((data, idx) => (
                              <Card key={idx} className="p-4 border border-gray-200">
                                <div className="flex justify-between items-start">
                                  <div className="flex-1">
                                    <p className="text-sm text-gray-700 mb-1">"{data.message}"</p>
                                    <p className="text-xs text-gray-500">{new Date(data.timestamp).toLocaleString()}</p>
                                  </div>
                                  <Badge className={`ml-3 ${data.tone === 'positive' ? 'bg-green-100 text-green-700' : data.tone === 'negative' ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-700'}`}>{data.tone}</Badge>
                                </div>
                              </Card>
                            ))}
                          </div>
                        )}
                      </TabsContent>
                    </Tabs>
                  </Card>
                </>
              )}
            </TabsContent>

            {/* ===== SESSIONS TAB ===== */}
            <TabsContent value="sessions" className="space-y-6">
              <div className="flex justify-between items-center">
                <h2 className="text-2xl font-bold text-gray-900">Counseling Sessions</h2>
                <Dialog open={showSessionDialog} onOpenChange={setShowSessionDialog}>
                  <DialogTrigger asChild>
                    <Button className="bg-gradient-to-r from-pink-500 to-rose-600" data-testid="record-session-btn">
                      <Plus className="w-4 h-4 mr-2" />Record Session
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-2xl">
                    <DialogHeader><DialogTitle>Record Counseling Session</DialogTitle></DialogHeader>
                    <div className="space-y-4 py-4">
                      <div>
                        <Label>Student *</Label>
                        <Select value={newSession.student_id} onValueChange={v => setNewSession({...newSession, student_id: v})}>
                          <SelectTrigger data-testid="session-student-select"><SelectValue placeholder="Select student" /></SelectTrigger>
                          <SelectContent>{students.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>Session Date *</Label>
                          <Input type="date" value={newSession.session_date} onChange={e => setNewSession({...newSession, session_date: e.target.value})} data-testid="session-date-input" />
                        </div>
                        <div>
                          <Label>Duration (minutes)</Label>
                          <Input type="number" value={newSession.duration_minutes} onChange={e => setNewSession({...newSession, duration_minutes: parseInt(e.target.value)})} />
                        </div>
                      </div>
                      <div>
                        <Label>Mood Assessment</Label>
                        <Select value={newSession.mood_assessment} onValueChange={v => setNewSession({...newSession, mood_assessment: v})}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="positive">Positive</SelectItem>
                            <SelectItem value="neutral">Neutral</SelectItem>
                            <SelectItem value="concerning">Concerning</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Session Notes *</Label>
                        <Textarea value={newSession.notes} onChange={e => setNewSession({...newSession, notes: e.target.value})} placeholder="Session notes and observations" rows={6} data-testid="session-notes-input" />
                      </div>
                    </div>
                    <DialogFooter>
                      <Button variant="outline" onClick={() => setShowSessionDialog(false)}>Cancel</Button>
                      <Button onClick={handleCreateSession} disabled={loading} data-testid="submit-session-btn">{loading ? 'Recording...' : 'Record Session'}</Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </div>
              {sessions.length === 0 ? (
                <Card className="p-12 text-center bg-white/90 backdrop-blur-sm">
                  <Calendar className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">No sessions recorded yet</p>
                </Card>
              ) : (
                <div className="grid gap-4">
                  {sessions.map(session => (
                    <Card key={session.id} className="p-6 bg-white/90 backdrop-blur-sm border-0 shadow-lg" data-testid={`session-${session.id}`}>
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h3 className="text-lg font-semibold text-gray-900">{session.student_name}</h3>
                            <Badge variant={session.mood_assessment === 'positive' ? 'default' : session.mood_assessment === 'concerning' ? 'destructive' : 'secondary'}>{session.mood_assessment}</Badge>
                          </div>
                          <p className="text-sm text-gray-600 mb-2 line-clamp-2">{session.notes}</p>
                          <div className="flex items-center gap-4 text-sm text-gray-500">
                            <span className="flex items-center gap-1"><Calendar className="w-4 h-4" />{new Date(session.session_date).toLocaleDateString()}</span>
                            <span>{session.duration_minutes} minutes</span>
                          </div>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>

            {/* ===== INTERVENTIONS TAB ===== */}
            <TabsContent value="interventions" className="space-y-6">
              <div className="flex justify-between items-center">
                <h2 className="text-2xl font-bold text-gray-900">Intervention Plans</h2>
                <Dialog open={showInterventionDialog} onOpenChange={setShowInterventionDialog}>
                  <DialogTrigger asChild>
                    <Button className="bg-gradient-to-r from-pink-500 to-rose-600" data-testid="create-intervention-btn">
                      <Plus className="w-4 h-4 mr-2" />Create Intervention
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-2xl">
                    <DialogHeader><DialogTitle>Create Intervention Plan</DialogTitle></DialogHeader>
                    <div className="space-y-4 py-4">
                      <div>
                        <Label>Student *</Label>
                        <Select value={newIntervention.student_id} onValueChange={v => setNewIntervention({...newIntervention, student_id: v})}>
                          <SelectTrigger data-testid="intervention-student-select"><SelectValue placeholder="Select student" /></SelectTrigger>
                          <SelectContent>{students.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Primary Concern *</Label>
                        <Textarea value={newIntervention.concern} onChange={e => setNewIntervention({...newIntervention, concern: e.target.value})} placeholder="Describe the primary concern" rows={3} data-testid="intervention-concern-input" />
                      </div>
                      <div>
                        <Label>Intervention Type</Label>
                        <Select value={newIntervention.intervention_type} onValueChange={v => setNewIntervention({...newIntervention, intervention_type: v})}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="academic">Academic</SelectItem>
                            <SelectItem value="emotional">Emotional</SelectItem>
                            <SelectItem value="behavioral">Behavioral</SelectItem>
                            <SelectItem value="social">Social</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Action Plan *</Label>
                        <Textarea value={newIntervention.action_plan} onChange={e => setNewIntervention({...newIntervention, action_plan: e.target.value})} placeholder="Detailed action plan and strategies" rows={4} data-testid="intervention-plan-input" />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div><Label>Start Date</Label><Input type="date" value={newIntervention.start_date} onChange={e => setNewIntervention({...newIntervention, start_date: e.target.value})} /></div>
                        <div><Label>Review Date</Label><Input type="date" value={newIntervention.review_date} onChange={e => setNewIntervention({...newIntervention, review_date: e.target.value})} /></div>
                      </div>
                    </div>
                    <DialogFooter>
                      <Button variant="outline" onClick={() => setShowInterventionDialog(false)}>Cancel</Button>
                      <Button onClick={handleCreateIntervention} disabled={loading} data-testid="submit-intervention-btn">{loading ? 'Creating...' : 'Create Intervention'}</Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </div>
              {interventions.length === 0 ? (
                <Card className="p-12 text-center bg-white/90 backdrop-blur-sm">
                  <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" /><p className="text-gray-600">No interventions created yet</p>
                </Card>
              ) : (
                <div className="grid gap-4">
                  {interventions.map(intervention => (
                    <Card key={intervention.id} className="p-6 bg-white/90 backdrop-blur-sm border-0 shadow-lg" data-testid={`intervention-${intervention.id}`}>
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h3 className="text-lg font-semibold text-gray-900">{intervention.student_name}</h3>
                            <Badge variant={intervention.status === 'active' ? 'default' : intervention.status === 'completed' ? 'secondary' : 'outline'}>{intervention.status}</Badge>
                            <Badge variant="outline">{intervention.intervention_type}</Badge>
                          </div>
                          <p className="text-sm font-medium text-gray-700 mb-1">Concern: {intervention.concern}</p>
                          <p className="text-sm text-gray-600 mb-2 line-clamp-2">{intervention.action_plan}</p>
                          <div className="flex items-center gap-4 text-sm text-gray-500">
                            <span>Start: {intervention.start_date ? new Date(intervention.start_date).toLocaleDateString() : 'Not set'}</span>
                            <span>Review: {intervention.review_date ? new Date(intervention.review_date).toLocaleDateString() : 'Not set'}</span>
                          </div>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>

            {/* ===== WELLNESS TAB ===== */}
            <TabsContent value="wellness">
              <WellnessPanel students={students} selectedStudent={selectedStudent} />
            </TabsContent>

            {/* ===== GAMIFICATION TAB ===== */}
            <TabsContent value="gamification">
              <CounselorGamificationView students={students} />
            </TabsContent>

            {/* ===== COMPLIANCE TAB ===== */}
            <TabsContent value="compliance">
              <CompliancePanel userRole="counselor" />
            </TabsContent>

            {/* ===== ANALYTICS TAB ===== */}
            <TabsContent value="analytics" className="space-y-6">
              <Card className="p-6 bg-white/90 backdrop-blur-sm border-0 shadow-lg">
                <h3 className="text-lg font-semibold mb-4">Counseling Overview</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="text-center">
                    <p className="text-3xl font-bold text-pink-600" data-testid="analytics-students">{students.length}</p>
                    <p className="text-sm text-gray-600">Total Students</p>
                  </div>
                  <div className="text-center">
                    <p className="text-3xl font-bold text-rose-600" data-testid="analytics-sessions">{sessions.length}</p>
                    <p className="text-sm text-gray-600">Total Sessions</p>
                  </div>
                  <div className="text-center">
                    <p className="text-3xl font-bold text-red-600" data-testid="analytics-active-interventions">{interventions.filter(i => i.status === 'active').length}</p>
                    <p className="text-sm text-gray-600">Active Interventions</p>
                  </div>
                </div>
              </Card>

              {/* AI Analytics Report Section */}
              <Card className="p-6 bg-gradient-to-r from-indigo-50 to-purple-50 border-indigo-200 shadow-lg">
                <div className="flex justify-between items-center mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-indigo-600 rounded-xl flex items-center justify-center">
                      <Brain className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-indigo-900">AI Analytics Report</h3>
                      <p className="text-xs text-indigo-600">Comprehensive analysis from wellness, gamification & sessions</p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      className="bg-indigo-600 hover:bg-indigo-700"
                      onClick={async () => {
                        setGeneratingReport(true);
                        try {
                          const res = await axios.post(`${API}/counselor/ai-analytics-report`);
                          setAiReport(res.data.report);
                          toast.success('AI report generated');
                        } catch (e) { toast.error(e.response?.data?.detail || 'Failed to generate report'); }
                        finally { setGeneratingReport(false); }
                      }}
                      disabled={generatingReport}
                      data-testid="generate-ai-report-btn"
                    >
                      {generatingReport ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Brain className="w-4 h-4 mr-2" />}
                      {generatingReport ? 'Analyzing...' : 'Generate AI Report'}
                    </Button>
                    <Button
                      variant="outline"
                      className="border-indigo-300 text-indigo-700 hover:bg-indigo-100"
                      onClick={async () => {
                        setDownloadingPdf(true);
                        try {
                          const res = await axios.post(`${API}/counselor/ai-analytics-report/pdf`, null, { responseType: 'blob' });
                          const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
                          const a = document.createElement('a');
                          a.href = url; a.download = 'counseling_analytics_report.pdf'; a.click();
                          URL.revokeObjectURL(url);
                          toast.success('PDF downloaded');
                        } catch { toast.error('Failed to download PDF'); }
                        finally { setDownloadingPdf(false); }
                      }}
                      disabled={downloadingPdf}
                      data-testid="download-pdf-btn"
                    >
                      {downloadingPdf ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                      PDF
                    </Button>
                  </div>
                </div>

                {generatingReport && (
                  <div className="text-center py-8">
                    <Loader2 className="w-10 h-10 animate-spin text-indigo-500 mx-auto mb-3" />
                    <p className="text-indigo-700 font-medium">Analyzing wellness, gamification & session data...</p>
                    <p className="text-xs text-indigo-500 mt-1">This may take a moment</p>
                  </div>
                )}

                {aiReport && !generatingReport && (
                  <div className="space-y-4" data-testid="ai-report-content">
                    {/* Risk Level Banner */}
                    <div className={`p-4 rounded-lg border-2 ${aiReport.overall_risk_level === 'high' ? 'bg-red-50 border-red-300' : aiReport.overall_risk_level === 'medium' ? 'bg-amber-50 border-amber-300' : 'bg-green-50 border-green-300'}`}>
                      <div className="flex justify-between items-center">
                        <div>
                          <p className="font-bold text-lg capitalize" data-testid="report-risk-level">Overall Risk: {aiReport.overall_risk_level}</p>
                          <p className="text-xs opacity-70">{aiReport.generated_date ? new Date(aiReport.generated_date).toLocaleString() : ''}</p>
                        </div>
                      </div>
                    </div>

                    {/* Executive Summary */}
                    <Card className="p-4">
                      <h4 className="font-semibold text-sm mb-2 text-indigo-700">Executive Summary</h4>
                      <p className="text-sm text-gray-700 leading-relaxed" data-testid="report-summary">{aiReport.executive_summary}</p>
                    </Card>

                    {/* Key Findings */}
                    {aiReport.key_findings?.length > 0 && (
                      <Card className="p-4">
                        <h4 className="font-semibold text-sm mb-2 text-indigo-700">Key Findings</h4>
                        <ul className="space-y-1">{aiReport.key_findings.map((f, i) => (
                          <li key={i} className="text-sm flex items-start gap-2"><span className="w-5 h-5 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center text-xs font-bold flex-shrink-0">{i + 1}</span>{f}</li>
                        ))}</ul>
                      </Card>
                    )}

                    {/* Student Analyses */}
                    {aiReport.student_analyses?.map((sa, idx) => (
                      <Card key={idx} className="p-4 border" data-testid={`student-analysis-${idx}`}>
                        <button className="w-full flex justify-between items-center" onClick={() => setExpandedStudent(expandedStudent === idx ? null : idx)}>
                          <div className="flex items-center gap-3">
                            <span className={`px-2 py-1 rounded text-xs font-bold ${sa.risk_level === 'high' ? 'bg-red-100 text-red-700' : sa.risk_level === 'medium' ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700'}`}>{sa.risk_level?.toUpperCase()}</span>
                            <span className="font-semibold">{sa.student_name}</span>
                          </div>
                          {expandedStudent === idx ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                        </button>
                        {expandedStudent === idx && (
                          <div className="mt-4 space-y-3 border-t pt-3">
                            {sa.emotional_wellbeing && (
                              <div><p className="text-xs font-semibold text-gray-500 mb-1">EMOTIONAL WELLBEING</p><div className="flex gap-2 mb-1"><Badge variant="outline" className="text-xs">Status: {sa.emotional_wellbeing.status}</Badge><Badge variant="outline" className="text-xs">Mood: {sa.emotional_wellbeing.mood_trend}</Badge></div>{sa.emotional_wellbeing.avg_energy_assessment && <p className="text-xs text-gray-600">Energy: {sa.emotional_wellbeing.avg_energy_assessment}</p>}{sa.emotional_wellbeing.key_observations?.map((o, i) => <p key={i} className="text-xs text-gray-600 ml-3">- {o}</p>)}</div>
                            )}
                            {sa.academic_engagement && (
                              <div><p className="text-xs font-semibold text-gray-500 mb-1">ACADEMIC ENGAGEMENT</p><div className="flex gap-2 mb-1"><Badge variant="outline" className="text-xs">Status: {sa.academic_engagement.status}</Badge></div>{sa.academic_engagement.xp_assessment && <p className="text-xs text-gray-600">{sa.academic_engagement.xp_assessment}</p>}{sa.academic_engagement.quiz_performance && <p className="text-xs text-gray-600">{sa.academic_engagement.quiz_performance}</p>}</div>
                            )}
                            {sa.behavioral_notes && (
                              <div><p className="text-xs font-semibold text-gray-500 mb-1">BEHAVIORAL</p>{sa.behavioral_notes.flags_summary && <p className="text-xs text-gray-600">Flags: {sa.behavioral_notes.flags_summary}</p>}{sa.behavioral_notes.intervention_status && <p className="text-xs text-gray-600">Interventions: {sa.behavioral_notes.intervention_status}</p>}{sa.behavioral_notes.referral_status && <p className="text-xs text-gray-600">Referrals: {sa.behavioral_notes.referral_status}</p>}</div>
                            )}
                            {sa.recommendations?.length > 0 && (
                              <div><p className="text-xs font-semibold text-gray-500 mb-1">RECOMMENDATIONS</p>{sa.recommendations.map((r, i) => <p key={i} className="text-xs text-gray-700 ml-3">- {r}</p>)}</div>
                            )}
                          </div>
                        )}
                      </Card>
                    ))}

                    {/* Trend Analysis */}
                    {aiReport.trend_analysis && (
                      <Card className="p-4">
                        <h4 className="font-semibold text-sm mb-3 text-indigo-700">Trend Analysis</h4>
                        <div className="space-y-3">
                          {aiReport.trend_analysis.emotional_trends && <div><p className="text-xs font-semibold text-gray-500">Emotional Trends</p><p className="text-sm text-gray-700">{aiReport.trend_analysis.emotional_trends}</p></div>}
                          {aiReport.trend_analysis.engagement_trends && <div><p className="text-xs font-semibold text-gray-500">Engagement Trends</p><p className="text-sm text-gray-700">{aiReport.trend_analysis.engagement_trends}</p></div>}
                          {aiReport.trend_analysis.intervention_effectiveness && <div><p className="text-xs font-semibold text-gray-500">Intervention Effectiveness</p><p className="text-sm text-gray-700">{aiReport.trend_analysis.intervention_effectiveness}</p></div>}
                        </div>
                      </Card>
                    )}

                    {/* Action Items */}
                    {aiReport.action_items?.length > 0 && (
                      <Card className="p-4">
                        <h4 className="font-semibold text-sm mb-3 text-indigo-700">Action Items</h4>
                        <div className="space-y-2">{aiReport.action_items.map((ai, i) => (
                          <div key={i} className="flex items-start gap-3 p-2 bg-gray-50 rounded-lg">
                            <Badge className={`text-xs flex-shrink-0 ${ai.priority === 'high' ? 'bg-red-100 text-red-700' : ai.priority === 'medium' ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700'}`}>{ai.priority}</Badge>
                            <div className="flex-1"><p className="text-sm">{ai.action}</p><p className="text-xs text-gray-400">Target: {ai.target_student} | {ai.timeline?.replace('_', ' ')}</p></div>
                          </div>
                        ))}</div>
                      </Card>
                    )}

                    {/* Recommendations for Counselor */}
                    {aiReport.recommendations_for_counselor?.length > 0 && (
                      <Card className="p-4 bg-indigo-50 border-indigo-200">
                        <h4 className="font-semibold text-sm mb-2 text-indigo-700">Recommendations for You</h4>
                        <ul className="space-y-1">{aiReport.recommendations_for_counselor.map((r, i) => <li key={i} className="text-sm text-indigo-800 flex items-start gap-2"><span className="text-indigo-400 mt-0.5">&#8226;</span>{r}</li>)}</ul>
                      </Card>
                    )}
                  </div>
                )}
              </Card>

              <Card className="p-6 bg-white/90 backdrop-blur-sm border-0 shadow-lg">
                <h3 className="text-lg font-semibold mb-4">Student Wellbeing Summary</h3>
                <div className="space-y-3">
                  {students.map(student => (
                    <div key={student.id} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg" data-testid={`analytics-student-${student.id}`}>
                      <span className="font-medium">{student.name}</span>
                      <Badge variant="outline">{student.grade}</Badge>
                    </div>
                  ))}
                </div>
              </Card>

              {/* Feedback Section */}
              <Card className="p-6 bg-white/90 backdrop-blur-sm border-0 shadow-lg">
                <div className="flex justify-between items-center mb-4">
                  <div className="flex items-center gap-3">
                    <MessageSquare className="w-5 h-5 text-pink-600" />
                    <h3 className="text-lg font-semibold">Feedback to Parents, Teachers & Students</h3>
                  </div>
                  <Button className="bg-gradient-to-r from-pink-500 to-rose-600" onClick={() => setShowFeedbackDialog(true)} data-testid="send-feedback-btn">
                    <Send className="w-4 h-4 mr-2" /> Send Feedback
                  </Button>
                </div>

                {sentFeedback.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <MessageSquare className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                    <p>No feedback sent yet. Click "Send Feedback" to share insights with parents, teachers, and students.</p>
                  </div>
                ) : (
                  <div className="space-y-3" data-testid="sent-feedback-list">
                    {sentFeedback.map(fb => (
                      <Card key={fb.id} className="p-4 border" data-testid={`sent-fb-${fb.id}`}>
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="font-semibold text-sm">Re: {fb.student_name}</span>
                              {fb.student_grade && <Badge variant="outline" className="text-xs">{fb.student_grade}</Badge>}
                              {fb.attached_report && <Badge variant="outline" className="text-xs"><Paperclip className="w-3 h-3 mr-1" />Report</Badge>}
                            </div>
                            <p className="text-sm text-gray-700 line-clamp-2">{fb.feedback_text}</p>
                            <div className="flex items-center gap-2 mt-2">
                              <span className="text-xs text-gray-400">Sent to:</span>
                              {fb.sent_to_student && <Badge className="bg-blue-100 text-blue-700 text-xs">Student</Badge>}
                              {fb.sent_to_parent && <Badge className="bg-green-100 text-green-700 text-xs">Parent</Badge>}
                              {fb.sent_to_teacher && <Badge className="bg-purple-100 text-purple-700 text-xs">Teacher</Badge>}
                            </div>
                          </div>
                          <span className="text-xs text-gray-400 ml-3 flex-shrink-0">{new Date(fb.created_at).toLocaleDateString()}</span>
                        </div>
                      </Card>
                    ))}
                  </div>
                )}
              </Card>

              {/* Feedback Dialog */}
              <Dialog open={showFeedbackDialog} onOpenChange={setShowFeedbackDialog}>
                <DialogContent className="max-w-2xl">
                  <DialogHeader><DialogTitle className="flex items-center gap-2"><Send className="w-5 h-5 text-pink-600" /> Send Feedback</DialogTitle></DialogHeader>
                  <div className="space-y-4 py-2">
                    <div>
                      <Label>Select Student *</Label>
                      <Select value={feedbackForm.student_id} onValueChange={v => setFeedbackForm({...feedbackForm, student_id: v})}>
                        <SelectTrigger data-testid="feedback-student-select"><SelectValue placeholder="Choose student" /></SelectTrigger>
                        <SelectContent>{students.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label>Feedback *</Label>
                      <Textarea value={feedbackForm.feedback_text} onChange={e => setFeedbackForm({...feedbackForm, feedback_text: e.target.value})} placeholder="Write your counselor feedback, observations, and recommendations..." rows={6} data-testid="feedback-text-input" />
                    </div>

                    {aiReport && (
                      <div className="flex items-center gap-3 p-3 bg-indigo-50 rounded-lg border border-indigo-200">
                        <Checkbox
                          id="attach-report"
                          checked={feedbackForm.attach_report}
                          onCheckedChange={v => setFeedbackForm({...feedbackForm, attach_report: v, attach_task_report: v ? null : feedbackForm.attach_task_report})}
                          data-testid="attach-report-checkbox"
                        />
                        <label htmlFor="attach-report" className="text-sm cursor-pointer flex items-center gap-2">
                          <Paperclip className="w-4 h-4 text-indigo-600" />
                          Attach AI Analytics Report
                          <span className="text-xs text-indigo-500">(generated report will be included)</span>
                        </label>
                      </div>
                    )}

                    {/* Task Report Attachment */}
                    {counselorAssignments.some(a => a.submissions?.some(s => s.ai_evaluation)) && (
                      <div className="p-3 bg-purple-50 rounded-lg border border-purple-200">
                        <Label className="text-sm font-medium flex items-center gap-2 mb-2"><Brain className="w-4 h-4 text-purple-600" /> Attach Task Report</Label>
                        <Select value={feedbackForm.attach_task_report ? 'selected' : ''} onValueChange={(v) => {
                          if (v === '') { setFeedbackForm({...feedbackForm, attach_task_report: null}); return; }
                          const [aId, sId] = v.split('|');
                          const assignment = counselorAssignments.find(a => a.id === aId);
                          const sub = assignment?.submissions?.find(s => s.id === sId);
                          if (sub?.ai_evaluation) {
                            setFeedbackForm({...feedbackForm, attach_task_report: { report_title: `Task Report: ${assignment.title}`, student_name: sub.student_name, score: `${sub.score_percentage}%`, ...sub.ai_evaluation }, attach_report: false});
                          }
                        }}>
                          <SelectTrigger className="text-xs" data-testid="attach-task-report-select"><SelectValue placeholder="Select a task report to attach" /></SelectTrigger>
                          <SelectContent>
                            {counselorAssignments.flatMap(a => (a.submissions || []).filter(s => s.ai_evaluation).map(s => (
                              <SelectItem key={`${a.id}|${s.id}`} value={`${a.id}|${s.id}`}>{a.title} - {s.student_name} ({s.score_percentage}%)</SelectItem>
                            )))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}

                    <div className="space-y-3">
                      <Label className="text-base font-semibold">Send To *</Label>
                      <div className="grid grid-cols-3 gap-3">
                        <div
                          className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${feedbackForm.send_to_student ? 'border-blue-400 bg-blue-50' : 'border-gray-200 hover:border-gray-300'}`}
                          onClick={() => setFeedbackForm({...feedbackForm, send_to_student: !feedbackForm.send_to_student})}
                          data-testid="send-to-student-toggle"
                        >
                          <Checkbox checked={feedbackForm.send_to_student} onCheckedChange={v => setFeedbackForm({...feedbackForm, send_to_student: v})} />
                          <div><p className="text-sm font-medium">Student</p><p className="text-xs text-gray-500">The student directly</p></div>
                        </div>
                        <div
                          className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${feedbackForm.send_to_parent ? 'border-green-400 bg-green-50' : 'border-gray-200 hover:border-gray-300'}`}
                          onClick={() => setFeedbackForm({...feedbackForm, send_to_parent: !feedbackForm.send_to_parent})}
                          data-testid="send-to-parent-toggle"
                        >
                          <Checkbox checked={feedbackForm.send_to_parent} onCheckedChange={v => setFeedbackForm({...feedbackForm, send_to_parent: v})} />
                          <div><p className="text-sm font-medium">Parent</p><p className="text-xs text-gray-500">Mapped parent</p></div>
                        </div>
                        <div
                          className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${feedbackForm.send_to_teacher ? 'border-purple-400 bg-purple-50' : 'border-gray-200 hover:border-gray-300'}`}
                          onClick={() => setFeedbackForm({...feedbackForm, send_to_teacher: !feedbackForm.send_to_teacher})}
                          data-testid="send-to-teacher-toggle"
                        >
                          <Checkbox checked={feedbackForm.send_to_teacher} onCheckedChange={v => setFeedbackForm({...feedbackForm, send_to_teacher: v})} />
                          <div><p className="text-sm font-medium">Teacher</p><p className="text-xs text-gray-500">Mapped teacher</p></div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setShowFeedbackDialog(false)}>Cancel</Button>
                    <Button className="bg-gradient-to-r from-pink-500 to-rose-600" onClick={handleSendFeedback} disabled={sendingFeedback} data-testid="submit-feedback-btn">
                      {sendingFeedback ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                      {sendingFeedback ? 'Sending...' : 'Send Feedback'}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </TabsContent>
          </Tabs>
        )}
      </div>
    </div>
  );
};

export default CounselorDashboard;
